import { z } from 'zod';

/**
 * One state move the concierge stored mid-turn, with the skills the router served after it.
 * `from` is absent when the turn started without a state.
 */
export const SkillRouteTransitionSchema = z.object({
  from: z.string().optional(),
  to: z.string(),
  skills: z.array(z.string()),
  /** 0-based model call that first ran with the new state's playbooks. */
  step: z.number().int().nonnegative().optional(),
});

/**
 * The skill router's record of one reply: the turn-start decision plus every mid-turn move.
 * The server writes it as the "Skill routing" resource-metadata entry once the reply lands; the
 * chat's Debug Info reads it from there.
 */
export const SkillRouteSchema = z.object({
  skills: z.array(z.string()),
  reason: z.enum(['state', 'intent', 'state+intent', 'none']),
  state: z.string().optional(),
  classifierOutcome: z.enum(['ok', 'invalid', 'failed']).optional(),
  classifierMs: z.number().optional(),
  probabilities: z.record(z.string(), z.number()).optional(),
  skillVersions: z.record(z.string(), z.string()).optional(),
  transitions: z.array(SkillRouteTransitionSchema).optional(),
});
export type SkillRoute = z.infer<typeof SkillRouteSchema>;

/** Provider name the server stamps on the persisted "Skill routing" resource-metadata entry. */
export const SKILL_ROUTE_RESOURCE_PROVIDER = 'skill-router';

/** Reads the persisted skill route out of resource-metadata entries, or `undefined` when none was recorded. */
export function parseSkillRouteResource(
  resources: ReadonlyArray<{ provider?: string; metadata?: unknown }>,
): SkillRoute | undefined {
  const entry = resources.find((resource) => resource.provider === SKILL_ROUTE_RESOURCE_PROVIDER);
  if (!entry) return;
  const parsed = SkillRouteSchema.safeParse(entry.metadata);
  return parsed.success ? parsed.data : undefined;
}
